import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { setAppLocales, essentialLocales, i18n } from '@fs/zion-locale'
import { Router } from '@fs/zion-router'
import { ApolloProvider, ApolloClient, InMemoryCache, HttpLink } from '@apollo/client'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { getSessionId } from '@fs/zion-session'
import { NoticeLoading } from '@fs/zion-icon'
import RebuggerClient from '@fs/zion-rebugger/AppClient'
import { FrontierBaseProviders } from '@fs/zion-root'
import * as serviceWorker from './serviceWorker'
import './locales'
import { PrisonProvider } from './Give/providers/prisonProvider/PrisonProvider'
import RootBase, { dynamicHeaders } from './shared/views/RootBase'
import DarkModeProvider from './shared/providers/darkModeProvider/DarkModeProvider'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities = process.env.NODE_ENV === 'development' ? (await import('./dev.activities')).default : undefined

// initialize Sentry for the app
if (sentryDSN) {
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// Create HttpLink with dynamic headers
const httpLink = new HttpLink({
  uri: '/records/images/orchestration/',
  fetch: (uri, options) => {
    // Set headers dynamically for each request
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${getSessionId()}`,
      'Timezone-Offset': new Date().getTimezoneOffset(),
    }

    // Add dynamic headers if they are set
    if (dynamicHeaders.rmsendpointauthex) {
      options.headers.rmsendpointauthex = dynamicHeaders.rmsendpointauthex
    }

    if (i18n.language) {
      options.headers['User-Locale'] = i18n.language
    }

    return fetch(uri, options)
  },
})

// Initialize the client for making network requests to lightyear-data
// eslint-disable-next-line import/prefer-default-export
export const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      ImageViewerRecordType: {
        keyFields: ['apolloId'],
      },
    },
  }),
  link: httpLink,
})

// Add any app logic here as flags, languages and translations are now configured and available
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)

  return (
    <PrisonProvider>
      <ApolloProvider client={apolloClient}>
        <Router basename={base}>
          <DarkModeProvider>
            <RootBase />
          </DarkModeProvider>
        </Router>
      </ApolloProvider>
    </PrisonProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
        {process.env.NODE_ENV === 'development' && <RebuggerClient />}
      </FrontierBaseProviders>
    </Suspense>
  </React.StrictMode>
)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
